import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom-v5-compat';
import { Redirect } from 'react-router';
import { useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { fetchPredictiveAudienceGoalQuery } from 'api/queries/predictiveAudiences';
import { colors } from 'ui/theme';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useGraphql } from 'utils/hooks/useGraphql';
import { useCoreGoalsContext } from 'modules/predictiveGoals/components/contexts/CoreGoalsContext/CoreGoalsContext';
import { UpsertPageSkeleton } from 'modules/predictiveGoals/components/upsert/UpsertPageSkeleton';
import { GhostButton, BUTTON_SIZES, PrimaryButton } from 'ui/button';
import noop from 'utils/noop';
import { MutatePreviewUsers } from '../../previewUsers/MutatePreviewUsers';
import { useEditGoal } from './hooks/useEditGoal';
import PredictiveGoalQueryBuilder from '../../goalBuilder/PredictiveGoalQueryBuilder';
import { PredictiveGoalsHeader } from '../../PredictiveGoalsHeader';
import { ErrorStatusBanner } from '../../errors/ErrorStatusBanner';
import { createPageActions } from '../../createPageActions';
import { useGoalDeleteMutation } from '../../hooks/useGoalDeleteMutation';
import { DeleteModal } from '../../modals/DeleteModal';
import { EditModal } from '../../modals/EditModal';
import { monitorVocab } from '../../../observabilityUtils';
import { transformForCreation, validateQuery, isValidGoalProperty } from '../../../utils';
import {
  shouldDisableEditByStatus,
  toGoalVariables,
  useGoalManagePermissionVerifier,
} from '../../../goalUtils';
import { emptyGoal } from '../../contexts/PredictiveGoalsContext/contextUtils';
import {
  CANCEL_GOAL_UPDATE,
  DELETE_PREDICTIVE_GOALS_TOAST_MESSAGE,
  EDITING_GOAL_BANNER_MESSAGE,
  GOAL_DELETE_ERROR,
  PROVIDE_GOAL_NAME,
  PROVIDE_USER_FIELD,
  PROVIDE_VALID_GOAL_NAME,
  TEST_IDS,
  UPDATE_GOAL,
} from '../../../constants';
import {
  EditPredictiveGoalVariables,
  GoalStatus,
  PredictiveAudienceGoal,
  ReadPredictiveGoalResponse,
} from '../../../types';
import {
  CreateOrEditActionFooter,
  CreateOrEditActionLayout,
  CreatedAndEditGoalCriteriaWrapper,
  EditInfoBanner,
  MainContentLayout,
  MainLayoutWrapper,
  StyledFullWidth,
  SpacedErrorBanner,
  SpacedFirstBanner,
} from '../../Styled';

const EditGoalPage = () => {
  const dispatch = useDispatch();
  const { goalId: goalIdStr } = useParams<{ goalId: string }>();
  const goalId = parseInt(goalIdStr as string, 10);
  const navigate = useNavigate();
  const [goal, setGoal] = useState({} as PredictiveAudienceGoal);
  const [goalName, setGoalName] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [goalQuery, setGoalQuery] = useState(emptyGoal);
  const [contactProperty, setContactProperty] = useState('');
  const [hasSaveError, setHasSaveError] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [submitButtonClicked, setSubmitButtonClicked] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewGoalChange, setIsNewGoalChange] = useState(false);
  const canManageGoals = useGoalManagePermissionVerifier();

  // toGoalVariables
  const {
    vocab: { vocabData },
    isLoading: isCoreDataLoading,
    areAllReady,
  } = useCoreGoalsContext();
  const { isLoading: loadingGoal, error: loadingError } = useGraphql<ReadPredictiveGoalResponse>(
    fetchPredictiveAudienceGoalQuery,
    {
      variables: { id: goalId },
      onCompleted: ({ predictiveAudienceGoal = {} as PredictiveAudienceGoal }) => {
        setGoal(predictiveAudienceGoal);
      },
    },
  );
  const {
    confirmUpdate,
    isUpdatePreviewError,
    isUpdatePreviewInvalid,
    isUpdatingGoal,
    updateGoal: executeEditGoal,
    usersCount,
  } = useEditGoal({
    hasCriteriaChanged: isNewGoalChange,
    onPreviewComplete: () => {
      setIsEditModalOpen(true);
    },
    onPreviewError: () => {
      setHasUnsavedChanges(true);
    },
    setGoal,
    setHasUpdateError: setHasSaveError,
    setUnsavedChanges: setHasUnsavedChanges,
  });

  useEffect(() => {
    if (areAllReady && !vocabData) {
      monitorVocab(vocabData);
    }
  }, [vocabData, areAllReady]);

  const [deleteGoalQuery] = useGoalDeleteMutation(
    (deletedGoal) => {
      setGoal(deletedGoal);
      dispatch(addPositiveToast(DELETE_PREDICTIVE_GOALS_TOAST_MESSAGE));
    },
    () => {
      dispatch(addNegativeToast(GOAL_DELETE_ERROR));
    },
  );

  /*
    On upserting the goal, ensure the user fields mapped to the goal properties are updated
  */
  useEffect(() => {
    // We aren't interested in the default, empty goal
    if (isEmpty(goal)) {
      return;
    }

    setGoalName(goal.name);
    setGoalQuery(goal.goal);
    setContactProperty(goal.userProfileField);
    setGoalDescription(goal.description);
  }, [goal]);

  /*
    On the query changing, track those changes depending on what has changed
  */
  useEffect(() => {
    // We aren't interested in the default, empty goal
    if (isEmpty(goal)) {
      return;
    }

    const queryChanged = goalQuery !== goal.goal;
    if (queryChanged) {
      setIsNewGoalChange(true);
    }
    if (queryChanged) {
      setHasUnsavedChanges(true);
    }
  }, [goalQuery]);

  if (loadingGoal || isCoreDataLoading) {
    return (
      <StyledFullWidth>
        <PredictiveGoalsHeader
          background={colors.grey1}
          editableGoal={{} as EditPredictiveGoalVariables}
          goalName={''}
          isLoading
          isReadOnly
          pageActions={<></>}
        />
        <MainLayoutWrapper data-background-color={colors.white}>
          <MainContentLayout>
            <UpsertPageSkeleton testId="edit" />
          </MainContentLayout>
        </MainLayoutWrapper>
        <CreateOrEditActionFooter>
          <CreateOrEditActionLayout>
            <GhostButton onClick={noop} size={BUTTON_SIZES.LARGE} isDisabled>
              {CANCEL_GOAL_UPDATE}
            </GhostButton>
            <PrimaryButton isLoading={false} isDisabled onClick={noop}>
              {UPDATE_GOAL}
            </PrimaryButton>
          </CreateOrEditActionLayout>
        </CreateOrEditActionFooter>
      </StyledFullWidth>
    );
  }
  let maybeErrorContent = null;
  if (loadingError) {
    maybeErrorContent = (
      <MainLayoutWrapper>
        <MainContentLayout>
          <SpacedErrorBanner>Unable to load goal!</SpacedErrorBanner>
        </MainContentLayout>
      </MainLayoutWrapper>
    );
  }
  if (hasSaveError) {
    maybeErrorContent = (
      <MainLayoutWrapper>
        <MainContentLayout>
          <SpacedErrorBanner>There was an error saving this form.</SpacedErrorBanner>
        </MainContentLayout>
      </MainLayoutWrapper>
    );
  }

  if (shouldDisableEditByStatus(goal)) {
    let redirectUrl = '/predictive-audiences';
    if (goal?.status === GoalStatus.DELETING) {
      redirectUrl = `/predictive-audiences/goals/${goal.id}`;
    }
    return <Redirect to={redirectUrl} />;
  }

  const showTrainingError =
    goal.status === GoalStatus.NEEDS_REVIEW || goal.status === GoalStatus.SYSTEM_ERROR;

  const maybeUpdateDisclaimer = !showTrainingError && (
    <MainLayoutWrapper>
      <MainContentLayout>
        <EditInfoBanner title="Heads up! This might take a while (no more than 24 hours).">
          {EDITING_GOAL_BANNER_MESSAGE}
        </EditInfoBanner>
      </MainContentLayout>
    </MainLayoutWrapper>
  );

  const validateGoal = () => {
    setSubmitButtonClicked(true);
    const errors = validateQuery(goalQuery);
    if (!goalName) {
      errors.add(PROVIDE_GOAL_NAME);
    } else if (!contactProperty) {
      errors.add(PROVIDE_USER_FIELD);
    }

    if (!isValidGoalProperty(goalName)) {
      errors.add(PROVIDE_VALID_GOAL_NAME);
    }
    return errors;
  };
  const displayErrors = (errors: Set<string>) => {
    if (errors.size <= 0) {
      return;
    }
    errors.forEach((err) => dispatch(addNegativeToast(err)));
  };
  const saveGoal = () => {
    const listIds: number[] = [];
    setHasSaveError(false);
    setHasUnsavedChanges(false);
    const transformedGoal = transformForCreation(goalQuery);
    executeEditGoal({
      id: goalId,
      name: goalName,
      userProfileField: contactProperty,
      goal: transformedGoal,
      description: goalDescription,
      listIds,
    });
  };

  /* Modal handlers for criteria change */
  const onEditActionModal = () => {
    const errors = validateGoal();
    if (errors.size) {
      displayErrors(errors);
      return;
    }
    saveGoal();
  };
  const editGoal = () => {
    confirmUpdate();
    setIsEditModalOpen(false);
  };

  /* Handler for non-criteria change */
  const onEditActionPassThrough = () => {
    const errors = validateGoal();
    if (errors.size) {
      displayErrors(errors);
      return;
    }
    saveGoal();
  };
  const onUpdateConfirm = isNewGoalChange ? onEditActionModal : onEditActionPassThrough;

  const deleteGoal = () => {
    deleteGoalQuery(goalId);
    setIsDeleteModalOpen(false);
  };
  const onDeleteAction = () => {
    setIsDeleteModalOpen(true);
  };
  const PageActions = createPageActions({
    isDeleteDisabled: false,
    isEditDisabled: true,
    onDelete: onDeleteAction,
    onEdit: () => null,
    canManageGoals,
  });

  const editGoalModal = isEditModalOpen && (
    <EditModal
      isModalOpen={true}
      onClose={() => setIsEditModalOpen(false)}
      editGoal={() => editGoal()}
      goal={goal}
    />
  );
  const deleteGoalModal = isDeleteModalOpen && (
    <DeleteModal
      isModalOpen={isDeleteModalOpen}
      onClose={() => setIsDeleteModalOpen(false)}
      deleteGoal={deleteGoal}
      goal={goal}
    />
  );

  const onCancel = () => {
    navigate(`/predictive-audiences`);
  };

  const goalVariables = toGoalVariables(goal);

  return (
    <>
      <StyledFullWidth>
        <PredictiveGoalsHeader
          background={colors.grey1}
          pageActions={<PageActions />}
          editableGoal={goalVariables}
          goalName={goalName}
          onUpdateGoalName={setGoalName}
        />
        <SpacedFirstBanner>
          {maybeErrorContent}
          {maybeUpdateDisclaimer}
          {showTrainingError && (
            <MainLayoutWrapper>
              <MainContentLayout>
                <ErrorStatusBanner goal={goal} />
              </MainContentLayout>
            </MainLayoutWrapper>
          )}
        </SpacedFirstBanner>
        <CreatedAndEditGoalCriteriaWrapper>
          <PredictiveGoalQueryBuilder
            contactProperty={contactProperty}
            canEditName={false}
            goalDescription={goalDescription}
            goalName={goalName}
            goalQuery={goalQuery}
            hasUnsavedChanges={hasUnsavedChanges}
            setGoalDescription={setGoalDescription}
            setGoalName={setGoalName}
            setGoalQuery={setGoalQuery}
            submitButtonClicked={submitButtonClicked}
            transformQuery={transformForCreation}
            vocabulary={vocabData}
          />
        </CreatedAndEditGoalCriteriaWrapper>
        <CreateOrEditActionFooter>
          <CreateOrEditActionLayout>
            <GhostButton onClick={onCancel} size={BUTTON_SIZES.LARGE}>
              {CANCEL_GOAL_UPDATE}
            </GhostButton>
            <MutatePreviewUsers
              primaryButtonConfig={{ text: UPDATE_GOAL, testId: TEST_IDS.CreateGoalBtn }}
              isDisabled={!!loadingError || isUpdatingGoal}
              isLoading={isUpdatingGoal}
              onClick={onUpdateConfirm}
              showPreviewContent={isUpdatePreviewError || isUpdatePreviewInvalid}
              isError={isUpdatePreviewError}
              usersCount={usersCount}
            />
          </CreateOrEditActionLayout>
        </CreateOrEditActionFooter>
        {editGoalModal}
        {deleteGoalModal}
      </StyledFullWidth>
    </>
  );
};

export { EditGoalPage };
